import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { PopoverContentContainer, PopoverRoot } from '@/components/Popover';
import { usePopover } from '@/hooks/popover/usePopover';
import Menu from '@/images/MenuDots.svg';
import TrashBin from '@/images/TrashBin.svg';
import { useUpdateAgent } from '@/pages/agents/api';
import { useUpdateAgentLoader } from '@/pages/agents/hooks';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AgentCreateTabType, QuestionsOperationType } from '@d-id/types';
import styled from '@emotion/styled';
import { FloatingArrow } from '@floating-ui/react';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { StarterQuestionForm } from './StarterQuestionForm';

const textStyles = `
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: -0.32px;
`;

const TextBlock: any = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-left: 8px;
`;
TextBlock.Title = styled.div`
    ${textStyles};
    color: ${theme.colors.black[900]};
`;
TextBlock.Text = styled.div`
    ${textStyles};
    color: ${theme.colors.black[600]};
    font-size: 14px;
    letter-spacing: -0.28px;
`;

const QuestionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const UlStyled = styled.ul`
    list-style: none;
    padding-left: 0;
    margin: 0;
`;

const LiStyled = styled.li`
    display: flex;
    align-items: center;
    margin-top: 8px;

    text-wrap: nowrap;

    &:first-child {
        margin-top: 0;
    }

    &:last-child {
        margin-bottom: 8px;
    }
`;

const DraggerContainer = styled.div<{ open: boolean }>`
    position: relative;
    width: 0;
    right: 19px;

    visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
`;

const AddQuestionButton = styled.button`
    padding: 0;
    margin: 0;
    width: max-content;

    color: black;
    background: none;
    border: none;
    outline: none;

    cursor: pointer;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;

    :hover {
        font-weight: 600;
    }

    :disabled {
        color: ${theme.colors.black.disabled};
    }
`;

const STARTER_MESSAGES_LIMIT = 4;

export const StarterQuestions = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const { mutateAsync: updateAgent } = useUpdateAgent();
    const { showAgentLoader, hideAgentLoader } = useUpdateAgentLoader();
    const { isAnonymous } = useAuth();

    const { isPopoverOpen, refs, floatingStyles, getFloatingProps, getReferenceProps, arrowProps } = usePopover({
        type: 'hover',
        options: { placement: 'top' },
        offsetOptions: { mainAxis: 5 },
    });

    const handleQuestionsSave = useCallback(
        (newQuestions?: string[]) => {
            newQuestions = newQuestions?.filter(Boolean);
            if (!newQuestions) {
                return;
            }

            if (isAnonymous) {
                console.warn('Skipping questions save: Not allowed for anonymous users or when playground is disabled');
                return;
            }

            showAgentLoader();

            updateAgent({
                agentId: agentEditorStore.inputAgent.id,
                update: { starter_message: newQuestions },
            })
                .then(() => {
                    (window as any).DID_AGENTS_API.updateAgent();
                })
                .catch(logger.error)
                .finally(() => {
                    if (agentEditorStore.isPlaygroundLoaded) {
                        hideAgentLoader();
                    }
                });
        },
        [isAnonymous, agentEditorStore.initAgentState]
    );

    const mixpanel = useAnalytics();
    function handleOnDragEnd(result: any) {
        if (!result.destination) return;

        const items = Array.from(agentEditorStore.starterQuestions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        agentEditorStore.setStarterQuestions(items);
        handleQuestionsSave(items);
    }

    const onSubmitQuestions = useCallback(
        (value: { type: QuestionsOperationType; question: string; prevQuestion?: string }) => {
            let newQuestions;

            switch (value.type) {
                case QuestionsOperationType.Create: {
                    newQuestions = [...agentEditorStore.starterQuestions, value.question];
                    break;
                }
                case QuestionsOperationType.Edit: {
                    if (value.prevQuestion !== undefined) {
                        let index = agentEditorStore.starterQuestions.indexOf(value.prevQuestion);
                        if (index !== -1) {
                            newQuestions = [...agentEditorStore.starterQuestions];
                            newQuestions[index] = value.question;
                        }
                    }
                    break;
                }
                default:
                    break;
            }

            if (newQuestions) {
                agentEditorStore.setStarterQuestions(newQuestions);
                handleQuestionsSave(newQuestions);
                mixpanel?.track('agent-editor', {
                    event: 'click',
                    page: AgentCreateTabType.ChatSettings,
                    from: 'Starter question',
                    action: newQuestions.length > agentEditorStore.starterQuestions.length ? 'added' : 'edited',
                });
            }
        },
        [agentEditorStore.starterQuestions, handleQuestionsSave]
    );

    const onDeleteQuestions = (value: string) => {
        const newQuestions = agentEditorStore.starterQuestions.filter(item => item !== value);
        agentEditorStore.setStarterQuestions(newQuestions);
        handleQuestionsSave(newQuestions);
        mixpanel?.track('agent-editor', {
            event: 'click',
            page: AgentCreateTabType.ChatSettings,
            from: 'Starter question',
            action: 'deleted',
        });
    };

    const questionLimitReached = agentEditorStore.starterQuestions.length >= STARTER_MESSAGES_LIMIT;
    const questionEmpty = agentEditorStore.starterQuestions.indexOf('') >= 0;
    const newQuestionDisabled = questionLimitReached || questionEmpty;

    const onAddQuestion = useCallback(
        () => !newQuestionDisabled && onSubmitQuestions({ prevQuestion: '', question: '', type: QuestionsOperationType.Create }),
        [newQuestionDisabled, onSubmitQuestions]
    );

    const getDragDropContext = () => (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="questions">
                {(provided: any) => (
                    <UlStyled {...provided.droppableProps} ref={provided.innerRef}>
                        {agentEditorStore.starterQuestions.map((item, index) => {
                            return (
                                <Draggable key={item} draggableId={item} index={index}>
                                    {provided => {
                                        const [isHovered, setIsHovered] = useState(false);
                                        const hideTimeout = useRef<NodeJS.Timeout>();

                                        const handleMouseEnter = () => {
                                            if (hideTimeout.current) {
                                                clearTimeout(hideTimeout.current);
                                            }
                                            setIsHovered(true);
                                        };

                                        const handleMouseLeave = () => {
                                            // delay to prevent flickering when hovering over the gap
                                            hideTimeout.current = setTimeout(() => {
                                                setIsHovered(false);
                                            }, 200);
                                        };

                                        return (
                                            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                                                <LiStyled ref={provided.innerRef} {...provided.draggableProps}>
                                                    <DraggerContainer
                                                        data-rbd-drag-handle-draggable-id={item}
                                                        {...provided.dragHandleProps}
                                                        open={isHovered}>
                                                        <MaskedIcon className="dragger-icon" size="16px" src={Menu} />
                                                    </DraggerContainer>
                                                    <StarterQuestionForm
                                                        onSubmit={onSubmitQuestions}
                                                        onDelete={onDeleteQuestions}
                                                        question={item}
                                                        questions={agentEditorStore.starterQuestions}
                                                        operationType={QuestionsOperationType.Edit}
                                                        onAddQuestion={onAddQuestion}
                                                    />
                                                    <MaskedIcon
                                                        cursor="pointer"
                                                        size="24px"
                                                        src={TrashBin}
                                                        onClick={() => onDeleteQuestions(item)}
                                                    />
                                                </LiStyled>
                                            </div>
                                        );
                                    }}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </UlStyled>
                )}
            </Droppable>
        </DragDropContext>
    );

    return (
        <>
            <QuestionsWrapper>
                {agentEditorStore.starterQuestions.length > 0 && getDragDropContext()}
                <AddQuestionButton
                    disabled={questionLimitReached}
                    role="button"
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    onClick={onAddQuestion}>
                    + Add question
                </AddQuestionButton>
            </QuestionsWrapper>
            {newQuestionDisabled && isPopoverOpen && (
                <PopoverRoot ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                    <FloatingArrow {...arrowProps} />
                    <PopoverContentContainer>
                        {!!questionLimitReached && `You can add up to ${STARTER_MESSAGES_LIMIT} questions`}
                        {!!questionEmpty && 'You have an empty question'}
                    </PopoverContentContainer>
                </PopoverRoot>
            )}
        </>
    );
});
